import { Box, Circle, Flex, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useToastAlert } from '~/hooks/toast/useToastAlerts'
import { TranslationKeys } from '~/locales/i18next'

import { CopyButton } from '@mangovoice/mango-ui'

import { ConnectionEventsList } from './ConnectionEventsList'
import { ExtensionsList } from './ExtensionsList'
import {
  HEALTH_COLOR,
  networkHealth,
  phoneServiceHealth,
  realtimeHealth,
} from './statusHealth'
import {
  type HealthLevel,
  useDiagnosticsSnapshot,
} from './useDiagnosticsSnapshot'
import { useHeartbeatAgo } from './useHeartbeatAgo'

// Map raw SIP enums to `statusPanel.*` translation keys for display. The
// Copy-diagnostics JSON still carries the raw values for engineering.
const REGISTRATION_KEY: Record<string, string> = {
  Initial: 'statusPanel.regNotStarted',
  Registered: 'statusPanel.regRegistered',
  Unregistered: 'statusPanel.regUnregistered',
  Terminated: 'statusPanel.regTerminated',
}
const TRANSPORT_KEY: Record<string, string> = {
  INITIAL: 'statusPanel.transportNotStarted',
  CONNECTED: 'statusPanel.transportConnected',
  DISCONNECTED: 'statusPanel.transportDisconnected',
}

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Flex justify="space-between" gap={4} py={1.5}>
    <Text fontSize="sm" color="fg.muted" flexShrink={0}>
      {label}
    </Text>
    <Text
      fontSize="sm"
      color="fg.default"
      fontFamily="mono"
      textAlign="right"
      wordBreak="break-word"
    >
      {value}
    </Text>
  </Flex>
)

const Section = ({
  title,
  level,
  children,
}: {
  title: string
  level?: HealthLevel
  children: React.ReactNode
}) => (
  <Box
    bg="bg.default"
    borderWidth="1px"
    borderColor="border.default"
    borderRadius="lg"
    px={5}
    py={4}
  >
    <Flex align="center" gap={2} mb={2}>
      {level && <Circle size="0.5rem" bg={HEALTH_COLOR[level]} />}
      <Text fontSize="sm" fontWeight="semibold" color="fg.default">
        {title}
      </Text>
    </Flex>
    {children}
  </Box>
)

export const StatusAdvancedView = () => {
  const { t } = useTranslation()
  const { toastSuccess, toastError } = useToastAlert()
  const snap = useDiagnosticsSnapshot()
  const heartbeat = useHeartbeatAgo(snap.websocket.lastPingAt)

  const getDiagnosticsText = () => {
    // Stamp the actual copy time so support sees when the payload was grabbed,
    // independent of capturedAt (which is frozen at panel-open).
    try {
      const payload = { ...snap, copiedAt: new Date().toISOString() }
      return JSON.stringify(payload, null, 2)
    } catch {
      // The snapshot is plain metadata so this shouldn't happen, but the copy
      // button is the core support flow — never let a serialization error
      // (e.g. an unexpected non-serializable value from an upstream atom) make
      // the button silently do nothing. Copy a sentinel the agent can report.
      return 'Error: could not serialize diagnostics'
    }
  }

  const registrationKey = REGISTRATION_KEY[snap.sip.registrationState]
  const transportKey = TRANSPORT_KEY[snap.sip.transportState]

  return (
    <Box maxW="44rem" mx="auto" w="100%" px={6} py={8}>
      <Flex justify="flex-end" mb={4}>
        <CopyButton
          value={getDiagnosticsText}
          text={t('statusPanel.copyDiagnostics')}
          copiedText={t('statusPanel.copied')}
          size="sm"
          variant="outline"
          iconSize={16}
          onCopied={() => toastSuccess(t('global.toast.copySuccess'))}
          onCopyError={() => toastError(t('global.toast.copyError'))}
        />
      </Flex>

      <Flex direction="column" gap={4}>
        <Section
          title={t('statusPanel.sectionSip')}
          level={phoneServiceHealth(snap).level}
        >
          <Row
            label={t('statusPanel.labelRegistration')}
            value={
              registrationKey
                ? t(registrationKey as TranslationKeys)
                : snap.sip.registrationState
            }
          />
          <Row
            label={t('statusPanel.labelTransport')}
            value={
              transportKey
                ? t(transportKey as TranslationKeys)
                : snap.sip.transportState
            }
          />
          {snap.sip.registrationError && (
            <Row
              label={t('statusPanel.labelLastError')}
              value={snap.sip.registrationError}
            />
          )}
        </Section>

        <Section
          title={t('statusPanel.sectionMessaging')}
          level={realtimeHealth(snap).level}
        >
          <Row
            label={t('statusPanel.labelWebSocket')}
            value={snap.websocket.connectionStatus}
          />
          <Row
            label={t('statusPanel.labelLastHeartbeat')}
            value={
              heartbeat.seconds === null
                ? t('statusPanel.heartbeatNone')
                : heartbeat.isStale
                  ? t('statusPanel.heartbeatStale', {
                      seconds: heartbeat.seconds,
                    })
                  : t('statusPanel.heartbeatAgo', {
                      seconds: heartbeat.seconds,
                    })
            }
          />
          <Row
            label={t('statusPanel.labelReconnectAttempts')}
            value={snap.websocket.reconnectAttempts}
          />
        </Section>

        <Section
          title={t('statusPanel.sectionNetwork')}
          level={networkHealth(snap).level}
        >
          <Row
            label={t('statusPanel.labelBrowser')}
            value={
              snap.network.online
                ? t('statusPanel.online')
                : t('statusPanel.offline')
            }
          />
        </Section>

        <Section title={t('statusPanel.sectionTelephony')}>
          <Row
            label={t('statusPanel.labelPbx')}
            value={snap.telephony.pbxName ?? '—'}
          />
          <Row
            label={t('statusPanel.labelPbxId')}
            value={snap.telephony.pbxId ?? '—'}
          />
          <Row
            label={t('statusPanel.labelPbxUuid')}
            value={snap.telephony.pbxUuid ?? '—'}
          />
          <Row
            label={t('statusPanel.labelDoNotDisturb')}
            value={
              snap.telephony.doNotDisturb
                ? t('statusPanel.on')
                : t('statusPanel.off')
            }
          />
          {/*
           * `respondBusyToSecondCall` maps from the `hasCallWaiting` setting,
           * a documented misnomer: true means "respond with busy to second
           * call" is ENABLED (call waiting effectively OFF). Labeled the way
           * the Phone Settings UI does to avoid showing the inverse.
           */}
          <Row
            label={t('statusPanel.labelRespondBusy')}
            value={
              snap.telephony.respondBusyToSecondCall
                ? t('statusPanel.on')
                : t('statusPanel.off')
            }
          />

          <ExtensionsList extensions={snap.telephony.extensions} />
        </Section>

        <Section title={t('statusPanel.sectionAudio')}>
          <Row
            label={t('statusPanel.labelMicPermission')}
            value={snap.audio.micPermission}
          />
          <Row
            label={t('statusPanel.labelMicInput')}
            value={
              snap.audio.inputCount === 0
                ? t('statusPanel.deviceNone')
                : (snap.audio.selectedInput ?? t('statusPanel.deviceDefault'))
            }
          />
          <Row
            label={t('statusPanel.labelSpeaker')}
            value={
              snap.audio.outputCount === 0
                ? t('statusPanel.deviceNone')
                : (snap.audio.selectedOutput ?? t('statusPanel.deviceDefault'))
            }
          />
        </Section>

        <Section title={t('statusPanel.sectionCall')}>
          {snap.call.active ? (
            <>
              <Row
                label={t('statusPanel.labelCallStatus')}
                value={snap.call.active.status}
              />
              <Row
                label={t('statusPanel.labelCallDirection')}
                value={snap.call.active.direction ?? '—'}
              />
              <Row
                label={t('statusPanel.labelCallSource')}
                value={snap.call.active.source ?? '—'}
              />
              <Row
                label={t('statusPanel.labelCallHold')}
                value={
                  snap.call.active.isOnHold
                    ? t('statusPanel.on')
                    : t('statusPanel.off')
                }
              />
              <Row
                label={t('statusPanel.labelCallMuted')}
                value={
                  snap.call.active.isMuted
                    ? t('statusPanel.on')
                    : t('statusPanel.off')
                }
              />
              <Row
                label={t('statusPanel.labelCallDuration')}
                value={
                  snap.call.active.durationSeconds === null
                    ? '—'
                    : `${snap.call.active.durationSeconds}s`
                }
              />
            </>
          ) : (
            <Text fontSize="sm" color="fg.muted">
              {t('statusPanel.callNone')}
            </Text>
          )}
        </Section>

        <Section title={t('statusPanel.sectionSession')}>
          <Row
            label={t('statusPanel.labelSignedIn')}
            value={
              snap.session.hasToken
                ? t('statusPanel.on')
                : t('statusPanel.tokenNone')
            }
          />
          {snap.session.tokenExpiresAt !== null && (
            <Row
              label={t('statusPanel.labelTokenExpiry')}
              value={`${new Date(
                snap.session.tokenExpiresAt,
              ).toISOString()} · ${
                snap.session.tokenExpired
                  ? t('statusPanel.tokenExpired')
                  : t('statusPanel.tokenValid')
              }`}
            />
          )}
        </Section>

        <Section title={t('statusPanel.sectionEnvironment')}>
          <Row
            label={t('statusPanel.labelAppVersion')}
            value={snap.environment.appVersion}
          />
          {snap.environment.latestVersion && (
            <Row
              label={t('statusPanel.labelLatestVersion')}
              value={`${snap.environment.latestVersion} · ${
                snap.environment.isClientOutOfDate
                  ? t('statusPanel.labelOutOfDate')
                  : t('statusPanel.labelUpToDate')
              }`}
            />
          )}
          <Row
            label={t('statusPanel.labelChannel')}
            value={snap.environment.channel ?? '—'}
          />
          <Row
            label={t('statusPanel.labelPlatform')}
            value={snap.environment.platform}
          />
          <Row
            label={t('statusPanel.labelBuild')}
            value={snap.environment.buildEnvironment}
          />
          <Row
            label={t('statusPanel.labelNotifications')}
            value={snap.environment.notificationPermission}
          />
          <Row
            label={t('statusPanel.labelLocalStorage')}
            value={
              snap.environment.localStorageWritable
                ? t('statusPanel.storageWritable')
                : t('statusPanel.storageBlocked')
            }
          />
          <Row
            label={t('statusPanel.labelCaptured')}
            value={snap.environment.capturedAt}
          />
          <Row
            label={t('statusPanel.labelUserAgent')}
            value={snap.environment.userAgent}
          />
        </Section>

        <Section title={t('statusPanel.sectionEvents')}>
          <ConnectionEventsList events={snap.events} />
        </Section>
      </Flex>
    </Box>
  )
}
